import { useReducer } from "react";
import { Link } from "react-router-dom";
import "./baccarat.css";

const HANDS = ["player", "banker"];
const HAND_LABELS = {
  player: "Player",
  banker: "Banker",
  tie: "Tie",
};
const OPENING_FLIP_ORDER = [
  ["player", 0],
  ["banker", 0],
  ["player", 1],
  ["banker", 1],
  ["player", 2],
  ["banker", 2],
];
const SUITS = [
  { id: "spades", label: "spades", symbol: "\u2660", tone: "black" },
  { id: "hearts", label: "hearts", symbol: "\u2665", tone: "red" },
  { id: "clubs", label: "clubs", symbol: "\u2663", tone: "black" },
  { id: "diamonds", label: "diamonds", symbol: "\u2666", tone: "red" },
];
const RANKS = [
  ["A", 1],
  ["2", 2],
  ["3", 3],
  ["4", 4],
  ["5", 5],
  ["6", 6],
  ["7", 7],
  ["8", 8],
  ["9", 9],
  ["10", 0],
  ["J", 0],
  ["Q", 0],
  ["K", 0],
];
const SHOE_DECKS = 8;
const BANKER_WIN_OPENING_CARDS = [
  { rank: "4", suitLabel: "hearts" },
  { rank: "5", suitLabel: "spades" },
  { rank: "2", suitLabel: "clubs" },
  { rank: "2", suitLabel: "diamonds" },
];

export function getHandTotal(cards) {
  return cards.reduce((sum, card) => sum + card.value, 0) % 10;
}

export function shouldPlayerDraw(playerTotal) {
  return playerTotal <= 5;
}

export function hasNatural(playerTotal, bankerTotal) {
  return playerTotal >= 8 || bankerTotal >= 8;
}

export function shouldBankerDraw(bankerTotal, playerThirdValue = null) {
  if (playerThirdValue === null) {
    return bankerTotal <= 5;
  }

  if (bankerTotal <= 2) {
    return true;
  }

  if (bankerTotal === 3) {
    return playerThirdValue !== 8;
  }

  if (bankerTotal === 4) {
    return playerThirdValue >= 2 && playerThirdValue <= 7;
  }

  if (bankerTotal === 5) {
    return playerThirdValue >= 4 && playerThirdValue <= 7;
  }

  if (bankerTotal === 6) {
    return playerThirdValue === 6 || playerThirdValue === 7;
  }

  return false;
}

export function getRoundOutcome(playerCards, bankerCards) {
  const playerTotal = getHandTotal(playerCards);
  const bankerTotal = getHandTotal(bankerCards);
  let winner = "tie";

  if (playerTotal > bankerTotal) {
    winner = "player";
  } else if (bankerTotal > playerTotal) {
    winner = "banker";
  }

  return {
    winner,
    playerTotal,
    bankerTotal,
  };
}

function createScore() {
  return {
    player: 0,
    banker: 0,
    tie: 0,
  };
}

function createShoe() {
  const cards = [];

  for (let deck = 0; deck < SHOE_DECKS; deck += 1) {
    for (const suit of SUITS) {
      for (const [rank, value] of RANKS) {
        cards.push({
          id: `${deck}-${suit.id}-${rank}`,
          rank,
          value,
          suit: suit.symbol,
          suitLabel: suit.label,
          tone: suit.tone,
        });
      }
    }
  }

  return cards;
}

function shuffleCards(cards) {
  const nextCards = [...cards];

  for (let index = nextCards.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [nextCards[index], nextCards[swapIndex]] = [
      nextCards[swapIndex],
      nextCards[index],
    ];
  }

  return nextCards;
}

function createShoeWithOpeningCards(openingCards) {
  const shoe = shuffleCards(createShoe());
  const chosenCards = openingCards.map((openingCard) => {
    const cardIndex = shoe.findIndex(
      (card) =>
        card.rank === openingCard.rank &&
        card.suitLabel === openingCard.suitLabel
    );

    return shoe.splice(cardIndex, 1)[0];
  });

  return [...chosenCards, ...shoe];
}

function getVisibleCards(cards) {
  return cards.filter((card) => card.isFaceUp);
}

function haveOpeningCards(cards) {
  return cards.length >= 2 && cards[0].isFaceUp && cards[1].isFaceUp;
}

function dealFaceDown(state, side) {
  const card = state.shoe[state.cursor];

  return {
    ...state,
    cursor: state.cursor + 1,
    lastDrawSide: side,
    hands: {
      ...state.hands,
      [side]: [
        ...state.hands[side],
        {
          ...card,
          isFaceUp: false,
          slotId: `${state.round}-${state.cursor}-${side}`,
        },
      ],
    },
  };
}

function formatOutcome(outcome) {
  if (outcome.winner === "tie") {
    return `Tie, ${outcome.playerTotal} to ${outcome.bankerTotal}`;
  }

  return `${HAND_LABELS[outcome.winner]} wins, ${outcome.playerTotal} to ${outcome.bankerTotal}`;
}

function finishRound(state, prefix = null) {
  const outcome = getRoundOutcome(state.hands.player, state.hands.banker);
  const score = {
    ...state.score,
    [outcome.winner]: state.score[outcome.winner] + 1,
  };
  const history = [
    {
      ...outcome,
      round: state.round,
    },
    ...state.history,
  ].slice(0, 12);

  return {
    ...state,
    phase: "complete",
    outcome,
    score,
    history,
    notice: prefix ? `${prefix} ${formatOutcome(outcome)}.` : `${formatOutcome(outcome)}.`,
  };
}

function advanceRound(state) {
  if (state.phase === "complete") {
    return state;
  }

  if (
    !haveOpeningCards(state.hands.player) ||
    !haveOpeningCards(state.hands.banker)
  ) {
    return {
      ...state,
      notice: "Flip the opening cards.",
      outcome: null,
    };
  }

  const playerOpeningTotal = getHandTotal(state.hands.player.slice(0, 2));
  const bankerOpeningTotal = getHandTotal(state.hands.banker.slice(0, 2));

  if (hasNatural(playerOpeningTotal, bankerOpeningTotal)) {
    return finishRound(state, "Natural.");
  }

  if (shouldPlayerDraw(playerOpeningTotal)) {
    if (state.hands.player.length < 3) {
      return {
        ...dealFaceDown(state, "player"),
        notice: "Player draws one face down.",
      };
    }

    if (!state.hands.player[2].isFaceUp) {
      return {
        ...state,
        notice: "Flip the player draw.",
      };
    }

    if (shouldBankerDraw(bankerOpeningTotal, state.hands.player[2].value)) {
      if (state.hands.banker.length < 3) {
        return {
          ...dealFaceDown(state, "banker"),
          notice: "Banker draws one face down.",
        };
      }

      if (!state.hands.banker[2].isFaceUp) {
        return {
          ...state,
          notice: "Flip the banker draw.",
        };
      }
    }

    return finishRound(state);
  }

  if (shouldBankerDraw(bankerOpeningTotal)) {
    if (state.hands.banker.length < 3) {
      return {
        ...dealFaceDown(state, "banker"),
        notice: "Banker draws one face down.",
      };
    }

    if (!state.hands.banker[2].isFaceUp) {
      return {
        ...state,
        notice: "Flip the banker draw.",
      };
    }
  }

  return finishRound(state);
}

function createRoundState({
  score = createScore(),
  history = [],
  round = 1,
  shoe = shuffleCards(createShoe()),
} = {}) {
  let state = {
    shoe,
    cursor: 0,
    round,
    phase: "playing",
    hands: {
      player: [],
      banker: [],
    },
    score,
    history,
    outcome: null,
    notice: "Flip the opening cards.",
    lastDrawSide: null,
  };

  state = dealFaceDown(state, "player");
  state = dealFaceDown(state, "banker");
  state = dealFaceDown(state, "player");
  state = dealFaceDown(state, "banker");

  return {
    ...advanceRound(state),
    lastDrawSide: null,
  };
}

function createBankerWinRoundState(options) {
  return createRoundState({
    ...options,
    shoe: createShoeWithOpeningCards(BANKER_WIN_OPENING_CARDS),
  });
}

function flipCard(hands, slotId) {
  let didFlip = false;
  const nextHands = HANDS.reduce((next, side) => {
    next[side] = hands[side].map((card) => {
      if (card.slotId !== slotId || card.isFaceUp) {
        return card;
      }

      didFlip = true;
      return {
        ...card,
        isFaceUp: true,
      };
    });

    return next;
  }, {});

  return didFlip ? nextHands : null;
}

function findNextHiddenCard(hands) {
  for (const [side, cardIndex] of OPENING_FLIP_ORDER) {
    const card = hands[side][cardIndex];

    if (card && !card.isFaceUp) {
      return card;
    }
  }

  return null;
}

function baccaratReducer(state, action) {
  if (action.type === "new-round") {
    return createRoundState({
      score: state.score,
      history: state.history,
      round: state.round + 1,
    });
  }

  if (action.type === "rig-banker") {
    return createBankerWinRoundState({
      score: state.score,
      history: state.history,
      round: state.phase === "complete" ? state.round + 1 : state.round,
    });
  }

  if (state.phase === "complete") {
    return state;
  }

  if (action.type === "flip-card") {
    const hands = flipCard(state.hands, action.slotId);
    return hands ? advanceRound({ ...state, hands }) : state;
  }

  if (action.type === "flip-next") {
    const nextCard = findNextHiddenCard(state.hands);
    const hands = nextCard ? flipCard(state.hands, nextCard.slotId) : null;
    return hands ? advanceRound({ ...state, hands }) : state;
  }

  return state;
}

function BaccaratCard({ card, onFlip, disabled }) {
  return (
    <button
      className={`baccaratCard baccaratCard${
        card.tone === "red" ? "Red" : "Black"
      }`}
      type="button"
      data-face-up={card.isFaceUp ? "true" : "false"}
      aria-label={
        card.isFaceUp
          ? `${card.rank} of ${card.suitLabel}, ${card.value} baccarat points`
          : "Face-down baccarat card"
      }
      disabled={disabled || card.isFaceUp}
      onClick={() => onFlip(card.slotId)}
    >
      <span className="baccaratCardInner">
        <span className="baccaratCardFace baccaratCardBack" aria-hidden="true">
          <span />
        </span>
        <span className="baccaratCardFace baccaratCardFront" aria-hidden="true">
          {card.isFaceUp && (
            <>
              <span className="baccaratCardCorner">
                <span>{card.rank}</span>
                <span>{card.suit}</span>
              </span>
              <span className="baccaratCardPip">{card.suit}</span>
              <span className="baccaratCardCorner baccaratCardCornerBottom">
                <span>{card.rank}</span>
                <span>{card.suit}</span>
              </span>
            </>
          )}
        </span>
      </span>
    </button>
  );
}

function BaccaratHand({ side, cards, phase, onFlip }) {
  const visibleCards = getVisibleCards(cards);
  const total = visibleCards.length > 0 ? getHandTotal(visibleCards) : null;

  return (
    <section className={`baccaratHand baccaratHand${HAND_LABELS[side]}`}>
      <header className="baccaratHandHeader">
        <span>{HAND_LABELS[side]}</span>
        <strong>{total === null ? "?" : total}</strong>
      </header>
      <div className="baccaratCards" aria-label={`${HAND_LABELS[side]} cards`}>
        {cards.map((card) => (
          <BaccaratCard
            key={card.slotId}
            card={card}
            disabled={phase === "complete"}
            onFlip={onFlip}
          />
        ))}
        {Array.from({ length: 3 - cards.length }).map((_, index) => (
          <span
            key={`placeholder-${index}`}
            className="baccaratCardPlaceholder"
            aria-hidden="true"
          />
        ))}
      </div>
    </section>
  );
}

function BaccaratHistory({ history }) {
  return (
    <div className="baccaratRoad" aria-label="Recent results">
      {history.length === 0 && <span className="baccaratRoadEmpty" />}
      {history.map((result) => (
        <span
          key={result.round}
          className={`baccaratRoadDot baccaratRoadDot${HAND_LABELS[result.winner]}`}
          title={`${HAND_LABELS[result.winner]} ${result.playerTotal}-${result.bankerTotal}`}
        >
          {HAND_LABELS[result.winner].slice(0, 1)}
        </span>
      ))}
    </div>
  );
}

function BaccaratPage() {
  const [state, dispatch] = useReducer(
    baccaratReducer,
    undefined,
    createRoundState
  );
  const nextHiddenCard = findNextHiddenCard(state.hands);

  function handleTableTouchStart(event) {
    if (event.touches.length < 3) {
      return;
    }

    event.preventDefault();
    dispatch({ type: "rig-banker" });
  }

  return (
    <main className="baccaratPage">
      <section className="baccaratShell" aria-label="Baccarat game">
        <header className="baccaratTopbar">
          <div>
            <Link className="baccaratHomeLink" to="/">
              Home
            </Link>
            <p className="baccaratEyebrow">Eight-deck shoe</p>
            <h1>Baccarat</h1>
          </div>
          <div className="baccaratScore" aria-label="Round score">
            <span>
              <strong>{state.score.player}</strong>
              Player
            </span>
            <span>
              <strong>{state.score.banker}</strong>
              Banker
            </span>
            <span>
              <strong>{state.score.tie}</strong>
              Tie
            </span>
          </div>
        </header>

        <section className="baccaratTable" aria-label="Baccarat table">
          <div className="baccaratFelt" onTouchStart={handleTableTouchStart}>
            <BaccaratHand
              side="player"
              cards={state.hands.player}
              phase={state.phase}
              onFlip={(slotId) => dispatch({ type: "flip-card", slotId })}
            />

            <div className="baccaratCenter">
              <span className="baccaratRound">Round {state.round}</span>
              <p aria-live="polite">{state.notice}</p>
              {state.outcome && (
                <strong
                  className={`baccaratOutcome baccaratOutcome${
                    HAND_LABELS[state.outcome.winner]
                  }`}
                >
                  {HAND_LABELS[state.outcome.winner]}
                </strong>
              )}
            </div>

            <BaccaratHand
              side="banker"
              cards={state.hands.banker}
              phase={state.phase}
              onFlip={(slotId) => dispatch({ type: "flip-card", slotId })}
            />
          </div>
        </section>

        <footer className="baccaratControls">
          <button
            className="baccaratPrimaryButton"
            type="button"
            onClick={() => dispatch({ type: "new-round" })}
          >
            New round
          </button>
          <button
            className="baccaratSecondaryButton"
            type="button"
            disabled={!nextHiddenCard || state.phase === "complete"}
            onClick={() => dispatch({ type: "flip-next" })}
          >
            Flip next
          </button>
          <BaccaratHistory history={state.history} />
        </footer>
      </section>
    </main>
  );
}

export default BaccaratPage;
